import type RootStore from "stores/RootStore";
import type ServiceStore from "stores/ServiceStore";
import { RouteType } from "types/RouteType";

/**
 * Install Daily Report — from the 2026-10-05 client meeting.
 *
 * Phase 1 of a port of the install foreman's own nine-tab app. Tabs 1-6 are
 * built; 7-9 and the eleven nested field-safety sub-tabs show their names and
 * an honest note.
 *
 * ── On `group: "Install"` and permissions ─────────────────────
 *
 * Grouped on Josh's ask, 2026-10-12: reaching a daily report by clicking
 * "Install Drawings" is not intuitive, and he wanted it in the nav and the
 * omnibar. Grouping also puts it in the omnibar's grouped quick-nav, which
 * skips routes with no group (AppLayoutStore.quickNavigationItems).
 *
 * Grouping costs nothing today. PermissionManager.hasRoleRouteAccess (:223)
 * does apply a RESTRICTIVE default to any route carrying a `group` — no
 * rolePermissions record for the path means a non-admin is denied (:248-258)
 * — but that method has NO live callers: both call sites are commented out,
 * at :113 inside checkRoutePermission and again at :321. The only check that
 * actually runs is `requiresAuth`. So the restrictive default is dead code,
 * and every signed-in user reaches this route regardless of role.
 *
 * ⚠ It becomes real the moment someone uncomments :113. At that point this
 * route AND Install Drawings both need a rolePermissions record per role, or
 * they close for non-admins — rolePermissions holds 10 rows and none of them
 * mentions /install. Whoever re-enables route permissions owns that, and it
 * is one piece of work covering both paths. Until then, do not describe this
 * route as permission-gated: it is not.
 */
const DailyReportRoute: RouteType = {
  name: "Daily Report",
  path: "/install/daily-report",
  title: "Daily Report",
  layout: "/app",
  group: "Install",
  requiresAuth: true,
  requiresAdmin: false,
  requiresPermission: false,
  viewportCard: {
    header: {
      component: () => import("./DailyReport.Page"),
      title: "Premier Precast Daily Report",
      enableRefreshButton: false,
      enableBackButton: true,
    },
    body: {
      component: () => import("./DailyReport.Body"),
      grayBackground: true,
    },
  },
  beforeEnter: async ({ rootStore }: { rootStore: RootStore }) => {
    try {
      const projectsStore = (
        rootStore as unknown as Record<string, ServiceStore | undefined>
      ).projectsStore;
      await Promise.allSettled([projectsStore?.find?.({ query: { $limit: 1000 } })]);
    } catch (error) {
      console.warn("[DailyReportRoute] warm-load failed:", error);
    }
    return true;
  },
};

export default DailyReportRoute;
